/**
 * Sprint 25 / S25-M — Frontend mirror of the BO anti-detection signal
 * catalog. The 16 keys + order here MUST stay byte-for-byte aligned with
 * `vpn-manage-server/src/common/anti_detection_signals.ts` so the matrix
 * axis labels, drill-down route params, and S23-G post-ship probe all
 * speak the same vocabulary.
 *
 * If the BO catalog changes, this file must change in the same Sprint —
 * a CI check in the parity test asserts the count + first key.
 */

export type AntiDetectionSignalKey =
  | 'refuse_invalid_ticket'
  | 'refuse_brand_mismatch'
  | 'refuse_acl_denied'
  | 'quarantine_outbound'
  | 'fm_handshake_rst'
  | 'fm_alpn_mismatch'
  | 'fm_reality_verify_fail'
  | 'fm_record_fragment_active'
  | 'connect_fail_tls'
  | 'connect_fail_udp_block'
  | 'slow_proxy_drop'
  | 'cdn_fallback'
  | 'decoy_jwt_hit'
  | 'decoy_ticket_hit'
  | 'audit_brand_scope_block'
  | 'audit_shared_infra_write';

export type AntiDetectionSignalBucket = 'A' | 'B' | 'C' | 'D';

export interface AntiDetectionSignalSpec {
  key: AntiDetectionSignalKey;
  label: string;
  bucket: AntiDetectionSignalBucket;
  source: string;
}

export const ANTI_DETECTION_SIGNAL_KEYS: readonly AntiDetectionSignalKey[] = [
  'refuse_invalid_ticket',
  'refuse_brand_mismatch',
  'refuse_acl_denied',
  'quarantine_outbound',
  'fm_handshake_rst',
  'fm_alpn_mismatch',
  'fm_reality_verify_fail',
  'fm_record_fragment_active',
  'connect_fail_tls',
  'connect_fail_udp_block',
  'slow_proxy_drop',
  'cdn_fallback',
  'decoy_jwt_hit',
  'decoy_ticket_hit',
  'audit_brand_scope_block',
  'audit_shared_infra_write',
];

export const ANTI_DETECTION_SIGNAL_COUNT = ANTI_DETECTION_SIGNAL_KEYS.length;

export const ANTI_DETECTION_SIGNAL_CATALOG: ReadonlyArray<AntiDetectionSignalSpec> =
  [
    {
      key: 'refuse_invalid_ticket',
      label: 'Refuse / invalid-ticket',
      bucket: 'A',
      source: 'connectivity_event:connect_fail.ticket_invalid',
    },
    {
      key: 'refuse_brand_mismatch',
      label: 'Refuse / brand-mismatch',
      bucket: 'A',
      source: 'audit_log:action=brand_mismatch_silent_filter',
    },
    {
      key: 'refuse_acl_denied',
      label: 'Refuse / ACL-denied',
      bucket: 'A',
      source: 'audit_log:action=tier_acl_denied',
    },
    {
      key: 'quarantine_outbound',
      label: 'Quarantine outbound',
      bucket: 'A',
      source: 'protocol_failure_cache + outbound_quarantine',
    },
    {
      key: 'fm_handshake_rst',
      label: 'FM / handshake-RST',
      bucket: 'B',
      source: 'fire_marker_event:tag=h',
    },
    {
      key: 'fm_alpn_mismatch',
      label: 'FM / ALPN-mismatch',
      bucket: 'B',
      source: 'fire_marker_event:tag=s',
    },
    {
      key: 'fm_reality_verify_fail',
      label: 'FM / Reality-verify-fail',
      bucket: 'B',
      source: 'fire_marker_event:tag=r',
    },
    {
      key: 'fm_record_fragment_active',
      label: 'FM / record-fragment',
      bucket: 'B',
      source: 'fire_marker_event:tag=rf',
    },
    {
      key: 'connect_fail_tls',
      label: 'Connect-fail / TLS',
      bucket: 'C',
      source: 'connectivity_event:connect_fail.tls',
    },
    {
      key: 'connect_fail_udp_block',
      label: 'Connect-fail / UDP-block',
      bucket: 'C',
      source: 'connectivity_event:connect_fail.udp_block',
    },
    {
      key: 'slow_proxy_drop',
      label: 'Slow-proxy drop',
      bucket: 'C',
      source: 'connectivity_event:slow_proxy_drop',
    },
    {
      key: 'cdn_fallback',
      label: 'CDN fallback',
      bucket: 'C',
      source: 'connectivity_event:cdn_fallback',
    },
    {
      key: 'decoy_jwt_hit',
      label: 'Decoy / JWT-hit',
      bucket: 'D',
      source: 'decoy_connection:tokenKind=jwt',
    },
    {
      key: 'decoy_ticket_hit',
      label: 'Decoy / ticket-hit',
      bucket: 'D',
      source: 'decoy_connection:tokenKind=connect-ticket',
    },
    {
      key: 'audit_brand_scope_block',
      label: 'Audit / brand-scope-block',
      bucket: 'D',
      source: 'audit_log:action=brand_scope_write_denied',
    },
    {
      key: 'audit_shared_infra_write',
      label: 'Audit / shared-infra-write',
      bucket: 'D',
      source: 'audit_log:action=shared_infra_write',
    },
  ];

/** Strong-correlation threshold — pairs above this magnitude light up red. */
export const STRONG_CORRELATION_THRESHOLD = 0.7;
/** Medium-correlation threshold — pairs above this light up orange. */
export const MEDIUM_CORRELATION_THRESHOLD = 0.5;
/** Weak-correlation threshold — pairs above this light up yellow. */
export const WEAK_CORRELATION_THRESHOLD = 0.3;

/**
 * Pick the heat colour for a correlation coefficient.
 *
 * Buckets:
 *   |r| >= 0.7   → red    (strong)
 *   |r| >= 0.5   → orange (medium)
 *   |r| >= 0.3   → yellow (weak)
 *   |r| <  0.3   → grey   (noise)
 *
 * Sign is encoded in saturation — positive r uses warmer tones, negative r
 * uses the same hue at slightly different lightness so the eye can still
 * pick up inverse-correlation clusters.
 */
export function correlationColor(r: number): string {
  const abs = Math.abs(r);
  if (!Number.isFinite(r)) return '#3f3f3f';
  if (abs >= STRONG_CORRELATION_THRESHOLD) {
    return r > 0 ? '#cf1322' : '#a8071a';
  }
  if (abs >= MEDIUM_CORRELATION_THRESHOLD) {
    return r > 0 ? '#fa8c16' : '#d4380d';
  }
  if (abs >= WEAK_CORRELATION_THRESHOLD) {
    return r > 0 ? '#fadb14' : '#d4b106';
  }
  return '#bfbfbf';
}

export function correlationBand(
  r: number
): 'strong' | 'medium' | 'weak' | 'noise' {
  const abs = Math.abs(r);
  if (abs >= STRONG_CORRELATION_THRESHOLD) return 'strong';
  if (abs >= MEDIUM_CORRELATION_THRESHOLD) return 'medium';
  if (abs >= WEAK_CORRELATION_THRESHOLD) return 'weak';
  return 'noise';
}
